import { FC, useEffect, useState } from 'react';

import { Lightswitch } from '../../Lightswitch/Lightswitch';
import { TextField } from '../../Inputs/Textfield/TextField';
import { LocationSelector } from '../../Inputs/LocationSelector/LocationSelector';
import { DeliverySelector } from '../../Inputs/DeliverySelector/DeliverySelector';
import { DatePickerInput } from '../../DatePickerInput/DatePickerInput';
import { TimePickerInput } from '../../TimePickerInput/TimePickerInput';
import { Icon } from '../../Icon/Icon';
import { format, getDate } from 'date-fns';

import { LineItem, StoreOrder, FormErrors } from '../../../SharedTypes/Models';

import './SlideInBody.scss';
import { DateTimePicker } from '../../DateTimePicker/DateTimePicker';
import { TextArea } from '../../Inputs/TextArea/TextArea';

export type Props = {
	isSaving: boolean;
	isCancelling: boolean;
	formValues: StoreOrder | null;
	formErrors: FormErrors;
	onFormChange: (updatedValues: Partial<StoreOrder>) => void;
	onOrderCancelled: (orderNumber: string) => void;
};

export const SlideInBody:  FC<Props> = ({isSaving, isCancelling, formValues, formErrors, onFormChange, onOrderCancelled}) => {
	if(formValues === null){
		return null;
	}

	// Order details ---------------------------------------------------------
    // Handle changes and send updates to parent
    const handleFieldChange = <T extends keyof StoreOrder>(fieldName: T, newValue: StoreOrder[T]) => {
        if(fieldName === 'status' && newValue){
          formValues['packedStatus'] = true;
        }
        formValues[fieldName] = newValue;
        onFormChange({ ...formValues });
    };

    const handleDeliveryTypeChanged = (newValue: string) => {
		formValues.deliveryType = newValue;
		onFormChange({ ...formValues });
    };

    const handleLocationChanged = (newValue: number) => {
        formValues.locationId = newValue;
        onFormChange({ ...formValues });
    };

	// Order lineitems ---------------------------------------------------------
	// Add a new order line
    const addOrderLine = () => {
		const id = (formValues.lineItems?.length ?? 0) + 1;
		
        const newLine = {
			id: 'new-line-' + id,
			orderId: 0,
			description: '', 
			isPayed: false, 
			qty: 1,
			planning: 0,
			plannedInPastry: 0,
			plannedInBakery: 0,
			approved: 0,
      preparedBakery: 0,
      preparedPastry: 0,
		};
		onFormChange({ lineItems: [...formValues.lineItems ?? [], newLine] });
    };

    // Remove an order line
    const removeOrderLine = (index: Number) => {
		const updatedLineItems = formValues.lineItems.filter((_, i) => i !== index);
		onFormChange({ lineItems: updatedLineItems });
    };

    // Update an order line
	const handleLineItemChange = (index: number, field: keyof LineItem, value: any) => {
		const updatedLineItems = [...formValues.lineItems];
		updatedLineItems[index] = { 
			...updatedLineItems[index], 
			[field]: value 
		};
		onFormChange({ lineItems: updatedLineItems });
	};

	// Handle spinner for qty
	const handleSpinner = (index: number, number: number) => {
		const updatedLineItems = [...formValues.lineItems];

		// Convert qty to number if somehow it is a string
		if(typeof updatedLineItems[index]['qty'] === 'string'){
			updatedLineItems[index]['qty'] = Number(updatedLineItems[index]['qty']);
		}

		if(updatedLineItems[index]['qty'] + number >= 1){
			updatedLineItems[index] = { 
				...updatedLineItems[index], 
				['qty']: updatedLineItems[index]['qty'] + number
			};
		}
		
		onFormChange({ lineItems: updatedLineItems });
    };

	// Cancel order ---------------------------------------------------------
	const cancelOrder = (orderNumber: string) => {
        onOrderCancelled(orderNumber);
    };

	const title = formValues.orderNumber ? `Ordre ${formValues.orderNumber}` : 'Ny bestilling';

	return(
		<div className={isSaving || isCancelling ? `c-slideInBody c-slideInBody--isSaving` : 'c-slideInBody'}>
			<div className='c-slideInBody__wrapper'>
				<h1 className='title'>{title}</h1>
				<div className='order-toggles'>
					<div className={`order-toggles__left order-toggles__left--${formValues.paidStatus}`}>
						<Lightswitch 
							value={formValues.paidStatus} 
							onToggle={(newValue) => handleFieldChange('paidStatus', newValue)}
							text='Betalt'
						/>
					</div>
          <div className={`order-toggles__middle order-toggles__middle--${formValues.packedStatus ? true : false}`}>
						<Lightswitch 
							value={formValues.packedStatus} 
							onToggle={(newValue) => handleFieldChange('packedStatus', newValue)}
							text='Pakket'
						/>
					</div>
					<div className={`order-toggles__right order-toggles__right--${formValues.status}`}>
						<Lightswitch 
							value={formValues.status} 
							onToggle={(newValue) => handleFieldChange('status', newValue)}
							text='Udleveret'
						/>
					</div>
				</div>
				<div className='order-details'>
					<div className='order-details__wrapper'>
						<TextField 
							value={formValues.customerName}
							errorMessage={formErrors.customerName}
							name="customerName" 
							placeholderText='Kunde' 
							type="text" 
							handleChange={(newValue) => handleFieldChange('customerName', newValue)}
						/>

						<div className='order-details__contact'>
							<TextField 
								value={formValues.attention} 
								name="attention" 
								placeholderText='Att.' 
								type="text" 
								handleChange={(newValue) => handleFieldChange('attention', newValue)}
							/>
							<TextField 
								value={formValues.phone} 
								name="phone" 
								placeholderText='Telefon' 
								type="text" 
								handleChange={(newValue) => handleFieldChange('phone', newValue)}
							/>
						</div>
						
						<div className='selector'>
							<span className='selector__label'>Afhentning</span>
							<DateTimePicker
								orderId={formValues.id}
								date={new Date(formValues.pickupTime)} 
								dateTimeChanged={(newPickupTime) => handleFieldChange('pickupTime', newPickupTime)}
								errors={formErrors}
							/>
						</div>

						<div className='selector'>
							<span className='selector__label'>Levering</span>
							<DeliverySelector
								deliveryType={formValues.deliveryType}
							  errorMessage={formErrors.deliveryType}
								handleChange={(newValue) => handleDeliveryTypeChanged(newValue)}
							/>
						</div>
						<div className='selector'>
							<span className='selector__label'>Butik</span>
							<LocationSelector 
								locationId={formValues.locationId}
								handleChange={(newValue) => handleLocationChanged(newValue)}
							/>
						</div>
						<TextField 
							value={formValues.receiverName}
							errorMessage={formErrors.receiverName}
							name="receiverName" 
							placeholderText='Vores ordremodtager' 
							type="text" 
							handleChange={(newValue) => handleFieldChange('receiverName', newValue)}
						/>
					</div>

				</div>

				<div className={formErrors.lineItems ? 'order-lineitems order-lineitems--error' : 'order-lineitems' }>
					<div className='order-lineitems__wrapper'>
						<div className='error-text'>{formErrors.lineItems}</div>
						<div className="header">
							<div className="header__column">Bestilling</div>
							<div className="header__column">Bestil</div>
							<div className="header__column header__column--last">Antal</div>
						</div>
						<div className="body">
							{formValues.lineItems && 
							formValues.lineItems.map((line, index) => (
								<div key={line.id} className='body__row'>
									<div className='data-item'>
										<TextArea
											value={line.description} 
											name="description-1" 
											placeholderText=''
											handleChange={(value) => handleLineItemChange(index, 'description', value)}
										/>
										{/* <TextField 
											value={line.description} 
											name="description-1" 
											placeholderText=''
											type="text" 
											handleChange={(value) => handleLineItemChange(index, 'description', value)}
											/> */}
									</div>
									<div className='data-item'>
										<Lightswitch
											value={line.planning == 1}
											onToggle={() => handleLineItemChange(index, 'planning', line.planning === 1 ? 0 : 1)}
											text=""
										/>
									</div>
									<div className='data-item data-item__spinner'>
										<button className="subtract" onClick={() => handleSpinner(index, -1)}>-</button>
										<TextField
											value={line.qty.toString()}
											name="qty"
											placeholderText=""
											type="number"
											handleChange={(value) => handleLineItemChange(index, 'qty', value)}
										/>
										<button className="add" onClick={() => handleSpinner(index, 1)}>+</button>
									</div>
									<div className='line-actions' onClick={() => removeOrderLine(index)}>
										<Icon name="TrashIcon" width={18} />
									</div>
								</div>
							))}
						</div>
						<div className='actions' onClick={addOrderLine}>
							<span>+</span>Ny linje
						</div>
					</div>
				</div>
				
				{
					formValues.orderNumber &&
					<div className='order-cancellation'>
						{
							!formValues.dateCancelled &&
							<div className='order-cancellation__cancel' onClick={() => cancelOrder(formValues.orderNumber ?? '0')}>Annuller ordre</div>
						}
						{
							formValues.dateCancelled &&
							<div className='order-cancellation__cancel-date'>
								Annulleret: {formValues.dateCancelled} 
								<span className="regret" onClick={() => cancelOrder(formValues.orderNumber ?? '0')}>Fortryd</span>
							</div>
						}

						<div className='order-cancellation__order-dates'>Oprettet: {formValues.dateCreated} - Ændret: {formValues.dateUpdated}</div>
					</div>
				}

			</div>
		</div>
	);
};

